import React, { useState, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Save, ArrowRight, Sparkles, ShoppingBag } from 'lucide-react';
import { Navbar } from '../components/layout/Navbar';
import { Button } from '../components/ui/Button';
import { IntegrationCard } from '../components/dashboard/IntegrationCard';
import { useToast } from '../components/ui/Toast';
import { googleSheets } from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../hooks/useLanguage';
export function SystemMessageConfigPage() {
  const {
    user,
    updateUser
  } = useAuth();
  const {
    t
  } = useLanguage();
  const {
    showToast
  } = useToast();
  const navigate = useNavigate();
  const [systemMessage, setSystemMessage] = useState(user?.systemMessage || 'أنت مساعد ذكي لخدمة العملاء. أجب بلباقة واحترافية.');
  const [isLoading, setIsLoading] = useState(false);
  // Mock integration states (in real app, fetch from backend)
  const [googleSheetsConnected, setGoogleSheetsConnected] = useState(false);
  const [shopifyConnected, setShopifyConnected] = useState(false);
  const handleSave = async () => {
    if (!user || !user.instanceName) {
      showToast('error', t('system.warning'));
      return;
    }
    setIsLoading(true);
    try {
      // Send instanceName and systemMessage to n8n webhook
      await googleSheets.saveConfig({
        ...user,
        instanceName: user.instanceName,
        systemMessage
      });
      updateUser({
        systemMessage
      });
      showToast('success', t('common.success') + '! ' + t('system.save'));
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (error: any) {
      console.error('Save error:', error);
      showToast('error', error.message || t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };
  const handleGoogleSheetsSave = async (data: Record<string, string>) => {
    try {
      console.log('Saving Google Sheets config:', data);
      await new Promise(resolve => setTimeout(resolve, 1000));
      setGoogleSheetsConnected(true);
      showToast('success', 'Google Sheets connected successfully!');
    } catch (error: any) {
      showToast('error', error.message);
      throw error;
    }
  };
  const handleGoogleSheetsTest = async () => {
    console.log('Testing Google Sheets connection');
    await new Promise(resolve => setTimeout(resolve, 1500));
    return true;
  };
  const handleGoogleSheetsDisconnect = async () => {
    console.log('Disconnecting Google Sheets');
    await new Promise(resolve => setTimeout(resolve, 500));
    setGoogleSheetsConnected(false);
    showToast('success', 'Google Sheets disconnected');
  };
  const handleShopifySave = async (data: Record<string, string>) => {
    try {
      console.log('Saving Shopify config:', data);
      await new Promise(resolve => setTimeout(resolve, 1000));
      setShopifyConnected(true);
      showToast('success', 'Shopify connected successfully!');
    } catch (error: any) {
      showToast('error', error.message);
      throw error;
    }
  };
  const handleShopifyTest = async () => {
    console.log('Testing Shopify connection');
    await new Promise(resolve => setTimeout(resolve, 1500));
    return true;
  };
  const handleShopifyDisconnect = async () => {
    console.log('Disconnecting Shopify');
    await new Promise(resolve => setTimeout(resolve, 500));
    setShopifyConnected(false);
    showToast('success', 'Shopify disconnected');
  };
  // Google Sheets Logo Component
  const GoogleSheetsLogo = () => <svg viewBox="0 0 87.3 78" className="w-6 h-6" data-id="element-2283">
      <path fill="#188038" d="M9.9 0L0 9.9v58.2l9.9 9.9h58.2l9.9-9.9V9.9L68.1 0H9.9z" data-id="element-2284" />
      <path fill="#fff" d="M61.2 14.4H26.1c-2.7 0-4.9 2.2-4.9 4.9v39.4c0 2.7 2.2 4.9 4.9 4.9h35.1c2.7 0 4.9-2.2 4.9-4.9V19.3c0-2.7-2.2-4.9-4.9-4.9zm-8.7 43.2H34.8v-7.6h17.7v7.6zm0-11.5H34.8v-7.6h17.7v7.6zm0-11.5H34.8v-7.6h17.7v7.6z" data-id="element-2285" />
    </svg>;
  // Shopify Logo Component
  const ShopifyLogo = () => <svg viewBox="0 0 109 124" className="w-6 h-6" data-id="element-2286">
      <path fill="#95BF47" d="M95.9 23.9c-.1-.9-.9-1.4-1.5-1.5-.6-.1-12.6-.2-12.6-.2s-10.1-9.8-11.1-10.8-3.3-.8-4.2-.5c0 0-2.2.7-5.8 1.9C58.3 6.6 54.5 2.3 48.9 2.3h-.4c-1.4-1.8-3.1-2.6-4.6-2.6C29.7-.3 22.3 24.8 20 34.6c-6.5 2-11.1 3.4-11.6 3.6-3.5 1.1-3.6 1.2-4.1 4.5-.3 2.5-9.5 73.3-9.5 73.3l75.6 13.1 36.6-9.1S96 24.8 95.9 23.9zM58.7 14.8c-2.5.8-5.3 1.7-8.4 2.6 0-5.3-.7-12.7-3.2-19 6.3.8 9.9 10.1 11.6 16.4zM47.4 18c-5.4 1.7-11.3 3.5-17.1 5.3 1.7-6.4 4.9-12.8 8.8-17 1.5-1.6 3.5-3.3 5.9-4.3 2.9 6.5 3.6 14.2 2.4 16zm-3.1-20.5c.9 0 1.8.3 2.6 1-2.7 1.1-5.2 3-7.2 5.2-5.2 5.7-9.1 14.9-10.4 23.5l-13.1 4c2.4-9.3 9.1-32.2 28.1-33.7z" data-id="element-2287" />
    </svg>;
  return <div className="min-h-screen bg-gray-50" dir={t('common.dir') === 'rtl' ? 'rtl' : 'ltr'} data-id="element-2288">
      <Navbar data-id="element-2289" />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-2290">
        {/* System Message Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-8" data-id="element-2291">
          <div className="p-8 border-b border-gray-100" data-id="element-2292">
            <div className="flex items-center gap-4 mb-2" data-id="element-2293">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center text-purple-600" data-id="element-2294">
                <Sparkles size={24} data-id="element-2295" />
              </div>
              <div data-id="element-2296">
                <h1 className="text-2xl font-bold text-gray-900" data-id="element-2297">
                  {t('system.title')}
                </h1>
                <p className="text-gray-500" data-id="element-2298">{t('system.subtitle')}</p>
              </div>
            </div>
          </div>

          <div className="p-8 space-y-6" data-id="element-2299">
            <div className="space-y-2" data-id="element-2300">
              <label className="block text-sm font-medium text-gray-700" data-id="element-2301">
                {t('system.label')}
              </label>
              <p className="text-xs text-gray-500 mb-2" data-id="element-2302">{t('system.help')}</p>
              <textarea value={systemMessage} onChange={e => setSystemMessage(e.target.value)} rows={10} className="block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20 transition-all duration-200 resize-y" placeholder="Example: You are a sales assistant for a real estate company. Your goal is to collect customer information and schedule a visit..." data-id="element-2303" />
              <div className={`text-xs text-gray-400 ${t('common.dir') === 'rtl' ? 'text-right' : 'text-left'}`} data-id="element-2304">
                {systemMessage.length} {t('common.characters')}
              </div>
            </div>

            {!user?.instanceName && <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800" data-id="element-2305">
                ⚠️ {t('system.warning')}
              </div>}

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-50" data-id="element-2306">
              <Button variant="secondary" onClick={() => navigate('/dashboard')} data-id="element-2307">
                {t('system.cancel')}
              </Button>
              <Button onClick={handleSave} isLoading={isLoading} disabled={!user?.instanceName} data-id="element-2308">
                <Save className={t('common.dir') === 'rtl' ? 'ml-2' : 'mr-2'} size={16} data-id="element-2309" />
                {t('system.save')}
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Integrations Section */}
        <div className="space-y-6" data-id="element-2310">
          <div data-id="element-2311">
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2312">
              {t('integrations.title')}
            </h2>
            <p className="text-gray-500" data-id="element-2313">{t('integrations.subtitle')}</p>
          </div>

          {/* Google Sheets Integration */}
          <IntegrationCard title="Google Sheets" description={t('integrations.googlesheets.desc')} icon={GoogleSheetsLogo} isConnected={googleSheetsConnected} fields={[{
          name: 'redirectUrl',
          label: 'OAuth Redirect URL',
          value: 'https://operiens.ai/oauth/google/callback',
          readonly: true,
          helpText: t('integrations.googlesheets.help')
        }, {
          name: 'clientId',
          label: 'Client ID *',
          placeholder: t('integrations.googlesheets.clientId'),
          type: 'text'
        }, {
          name: 'clientSecret',
          label: 'Client Secret *',
          placeholder: t('integrations.googlesheets.clientSecret'),
          type: 'password'
        }]} onSave={handleGoogleSheetsSave} onTest={handleGoogleSheetsTest} onDisconnect={googleSheetsConnected ? handleGoogleSheetsDisconnect : undefined} helpLink="/help/google-sheets-oauth" lastUpdated={googleSheetsConnected ? new Date().toISOString() : undefined} data-id="element-2314" />

          {/* Shopify Integration */}
          <IntegrationCard title="Shopify" description={t('integrations.shopify.desc')} icon={ShopifyLogo} isConnected={shopifyConnected} fields={[{
          name: 'storeUrl',
          label: 'Shopify Store URL *',
          placeholder: t('integrations.shopify.storeUrl'),
          type: 'text'
        }, {
          name: 'accessToken',
          label: 'Admin API Access Token *',
          placeholder: t('integrations.shopify.accessToken'),
          type: 'password'
        }]} onSave={handleShopifySave} onTest={handleShopifyTest} onDisconnect={shopifyConnected ? handleShopifyDisconnect : undefined} helpLink="/help/shopify-integration" lastUpdated={shopifyConnected ? new Date().toISOString() : undefined} data-id="element-2315" />
        </div>
      </main>
    </div>;
}